import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { AuditApiService } from '../../audit/services/audit-api.service';
import { AuditLogResponse } from '../../audit/models/audit.model';
import { AuditDisplayService } from '../../../shared/services/audit-display.service';
import { DiscountRuleResponse } from '../models/discount-rule.model';
import { ProductoApiService } from '../services/producto-api.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { AuditTimelineComponent } from '../../../shared/components/audit/audit-timeline.component';

type BadgeColor =
  | 'primary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark';

@Component({
  selector: 'app-discount-rule-detail-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    BadgeComponent,
    AuditTimelineComponent,
  ],
  templateUrl: './discount-rule-detail-modal.component.html',
})
export class DiscountRuleDetailModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() rule: DiscountRuleResponse | null = null;
  @Output() close = new EventEmitter<void>();

  // Audit timeline
  activeTab: 'info' | 'timeline' = 'info';
  timelineLogs: AuditLogResponse[] = [];
  loadingTimeline = false;
  timelineError = '';
  timelinePage = 0;
  timelineTotalPages = 0;
  timelineTotalElements = 0;

  businessId = 1;
  private productName: string | null = null;

  constructor(
    private datetimeService: DatetimeService,
    private auditApi: AuditApiService,
    private auditDisplay: AuditDisplayService,
    private productApi: ProductoApiService,
    private authService: AuthService,
  ) {
    // Inc 58: businessId real del usuario (el 1 fijo provocaba 403 en otros negocios).
    this.businessId = this.authService.getBusinessId() ?? this.businessId;
  }

  /**
   * Inc 58: reacciona también a un cambio de `rule` con el modal ya abierto; antes
   * solo miraba `isOpen` y el detalle se quedaba con los datos de la regla anterior.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!(changes['isOpen'] || changes['rule'])) return;
    if (!this.isOpen || !this.rule) return;

    this.businessId = this.authService.getBusinessId() ?? this.businessId;
    this.activeTab = 'info';
    this.timelineLogs = [];
    this.timelinePage = 0;
    this.resolverProducto();
  }

  onClose(): void {
    this.close.emit();
  }

  parseJson(value?: string): Record<string, unknown> | null {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  getLogDotColor(op: string): string {
    switch (op?.toUpperCase()) {
      case 'CREATE':
        return 'bg-success-500';
      case 'UPDATE':
        return 'bg-brand-500';
      case 'DELETE':
        return 'bg-error-500';
      default:
        return 'bg-gray-400';
    }
  }

  getRuleTypeLabel(ruleType: string): string {
    const labels: Record<string, string> = {
      volume: 'Volumen',
      contractual: 'Contractual',
      coupon: 'Cupón',
      min_price: 'Precio mínimo',
    };
    return labels[ruleType] || ruleType;
  }

  getStatusColor(): BadgeColor {
    if (this.rule?.isExpired) return 'error';
    if (this.rule?.usesRemaining === 0 && this.rule?.maxUses) return 'warning';
    return 'success';
  }

  getStatusLabel(): string {
    if (this.rule?.isExpired) return 'Expirado';
    if (this.rule?.usesRemaining === 0 && this.rule?.maxUses) return 'Agotado';
    return 'Activo';
  }

  getDiscountLabel(): string {
    if (!this.rule) return '-';
    if (
      this.rule.discountType === 'percent' &&
      this.rule.discountValue !== undefined
    )
      return `${this.rule.discountValue}%`;
    if (
      this.rule.discountType === 'fixed_amount' &&
      this.rule.discountValue !== undefined
    )
      return this.formatPrecio(this.rule.discountValue);
    if (this.rule.couponCode) return `Cupón: ${this.rule.couponCode}`;
    if (this.rule.minSalePrice !== undefined)
      return `Min. ${this.formatPrecio(this.rule.minSalePrice)}`;
    return '-';
  }

  formatPrecio(price?: number): string {
    if (price === undefined || price === null) return '-';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(price);
  }

  formatDate(dateStr?: string): string {
    return this.datetimeService.formatDateTime(dateStr);
  }

  formatDateOnly(dateStr?: string): string {
    if (!dateStr) return '-';
    const soloFecha = dateStr.split('T')[0];
    const partes = soloFecha.split('-');
    if (partes.length === 3) {
      const [y, m, d] = partes;
      return `${d}/${m}/${y}`;
    }
    return this.datetimeService.formatDateTime(dateStr);
  }

  //
  private resolverProducto(): void {
    this.productName = null;
    if (this.rule?.productName) {
      this.productName = this.rule.productName;
      return;
    }
    if (this.rule?.productId) {
      this.productApi
        .getProductById(this.rule.productId, this.businessId)
        .subscribe({
          next: (p) => (this.productName = p.name),
          error: () => (this.productName = `Producto #${this.rule?.productId}`),
        });
    }
  }

  getProductLabel(): string {
    if (this.productName) return this.productName;
    if (this.rule?.productId) return `Producto #${this.rule.productId}`;
    return 'Todos los productos';
  }

  //Timeline
  private readonly FIELD_LABELS: Record<string, string> = {
    ruleType: 'Tipo de regla',
    discountType: 'Tipo de descuento',
    discountValue: 'Valor del descuento',
    minQuantity: 'Cantidad mínima',
    maxQuantity: 'Cantidad máxima',
    couponCode: 'Código de cupón',
    maxUses: 'Máximo de usos',
    currentUses: 'Usos actuales',
    minSalePrice: 'Precio mínimo de venta',
    validFrom: 'Válido desde',
    validUntil: 'Válido hasta',
    productId: 'Producto',
    contactId: 'Contacto',
    description: 'Descripción',
    isActive: 'Disponible',
  };

  private readonly HIDDEN_FIELDS = [
    'id',
    'businessId',
    'datetimeCreated',
    'datetimeUpdated',
    'isExpired',
    'usesRemaining',
  ];

  private readonly RULE_TYPE_LABELS: Record<string, string> = {
    volume: 'Volumen',
    contractual: 'Contractual',
    coupon: 'Cupón',
    min_price: 'Precio mínimo',
  };
  private readonly DISCOUNT_TYPE_LABELS: Record<string, string> = {
    percent: 'Porcentaje',
    fixed_amount: 'Monto fijo',
  };
  private readonly BOOLEAN_FIELDS = ['isActive'];
  private readonly PERCENT_OR_MONEY = ['discountValue', 'minSalePrice'];

  private formatFieldValue(field: string, value: unknown): string {
    if (value === undefined || value === null || value === '') return '-';
    if (this.BOOLEAN_FIELDS.includes(field) || typeof value === 'boolean') {
      return value === true || value === 'true' ? 'Sí' : 'No';
    }
    if (field === 'ruleType')
      return this.RULE_TYPE_LABELS[String(value)] ?? String(value);
    if (field === 'discountType')
      return this.DISCOUNT_TYPE_LABELS[String(value)] ?? String(value);
    if (field === 'validFrom' || field === 'validUntil')
      return this.formatDateOnly(String(value));
    return String(value);
  }

  getOperationLabel(op: string): string {
    const o = op?.toUpperCase();
    if (o === 'INSERT' || o === 'CREATE') return 'CREADO';
    if (o === 'UPDATE') return 'ACTUALIZADO';
    if (o === 'DELETE') return 'ELIMINADO';
    return op;
  }

  getChangedValues(
    log: AuditLogResponse,
  ): Array<{ field: string; label: string; old: string; new: string }> {
    // Preferir el enriquecimiento del backend (changesDisplay); si es null, fallback técnico propio.
    const enriched = this.auditDisplay.displayRows(log);
    if (enriched !== null) {
      return enriched.map((r) => ({
        field: r.field,
        label: r.label,
        old: r.oldVal,
        new: r.newVal,
      }));
    }
    try {
      const oldObj =
        typeof log.oldValues === 'string'
          ? JSON.parse(log.oldValues)
          : log.oldValues || {};
      const newObj =
        typeof log.newValues === 'string'
          ? JSON.parse(log.newValues)
          : log.newValues || {};

      const fields: string[] =
        Array.isArray(log.changedFields) && log.changedFields.length > 0
          ? log.changedFields
          : Object.keys(newObj).filter(
              (k) => JSON.stringify(oldObj[k]) !== JSON.stringify(newObj[k]),
            );

      return fields
        .filter((f) => !this.HIDDEN_FIELDS.includes(f))
        .map((f) => ({
          field: f,
          label: this.FIELD_LABELS[f] || f,
          old: this.formatFieldValue(f, oldObj[f]),
          new: this.formatFieldValue(f, newObj[f]),
        }))
        .filter((f) => f.old !== f.new);
    } catch {
      return [];
    }
  }

  getUserLabel(log: AuditLogResponse): string {
    if (log.userFullName) return log.userFullName;
    if (log.username) return log.username;
    return `Usuario #${log.idUser}`;
  }

  onTabChange(tab: 'info' | 'timeline'): void {
    this.activeTab = tab;
    if (
      tab === 'timeline' &&
      this.timelineLogs.length === 0 &&
      !this.loadingTimeline
    ) {
      this.loadTimeline(0);
    }
  }

  loadTimeline(page = 0): void {
    if (!this.rule?.id) return;
    this.loadingTimeline = true;
    this.timelineError = '';
    this.timelinePage = page;

    this.auditApi
      .getLogs({
        entityName: 'product_discount_rule',
        idEntity: String(this.rule.id),
        page,
        size: 15,
      })
      .subscribe({
        next: (data) => {
          this.timelineLogs = data.content ?? [];
          this.timelinePage = data.page ?? page;
          this.timelineTotalPages = data.totalPages ?? 1;
          this.timelineTotalElements = data.totalElements ?? 0;
          this.loadingTimeline = false;
        },
        error: () => {
          this.timelineError = 'Error al cargar la línea de tiempo.';
          this.loadingTimeline = false;
        },
      });
  }
}
